import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import InviteForm from './components/InviteForm';
import EmploymentTermModal from './components/EmploymentTermModal';
import StaffOrganizationModal from './components/StaffOrganizationModal';
import ShiftPermissionModal from './components/ShiftPermissionModal';
import StaffProfileEditModal from './components/StaffProfileEditModal';

interface StaffMember {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  language: string;
  hired_at: string | null;
  is_active: boolean;
  role_name: string;
  department_id: string | null;
  department_name: string | null;
  job_title: string | null;
  access_level: 'super_admin' | 'operations_manager' | 'staff';
  department_ids: string[];
  department_names: string[];
  permissions: string[];
  default_landing_page: string | null;
}

const accessLevelLabel = (level: StaffMember['access_level']) => level === 'super_admin' ? '最高管理者' : level === 'operations_manager' ? '運用管理者' : '一般スタッフ';

export default function StaffPage() {
  const { profile: currentProfile } = useAuth();
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [expandedStaffId, setExpandedStaffId] = useState<string | null>(null);
  const [detailData, setDetailData] = useState<{
    employment_terms: Record<string, unknown> | null;
    document_status: Record<string, unknown> | null;
  }>({ employment_terms: null, document_status: null });
  const [detailLoading, setDetailLoading] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [payStaff, setPayStaff] = useState<StaffMember | null>(null);
  const [organizationStaff, setOrganizationStaff] = useState<StaffMember | null>(null);
  const [shiftPermissionStaff, setShiftPermissionStaff] = useState<StaffMember | null>(null);
  const [profileEditStaff, setProfileEditStaff] = useState<StaffMember | null>(null);

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [staffResult, accessResult] = await Promise.all([
        supabase.rpc('admin_list_staff_portal'),
        supabase.rpc('admin_get_staff_access_overview'),
      ]);

      if (staffResult.error) {
        setError('スタッフ情報の取得に失敗しました');
        return;
      }

      // Access metadata is additive. A temporary metadata/API cache error must not hide the staff list.
      const accessRows = (accessResult.error ? [] : (accessResult.data || [])) as Array<{ staff_id: string; access_level: StaffMember['access_level']; department_ids: string[]; department_names: string[]; permissions: string[]; default_landing_page: string | null }>;
      const accessMap = new Map(accessRows.map((row) => [row.staff_id, row]));
      const mapped = ((staffResult.data || []) as Omit<StaffMember, 'access_level' | 'department_ids' | 'department_names' | 'permissions' | 'default_landing_page'>[]).map((staff) => {
        const access = accessMap.get(staff.id);
        return { ...staff, access_level: access?.access_level || (staff.role_name === 'admin' ? 'super_admin' : 'staff'), department_ids: access?.department_ids || (staff.department_id ? [staff.department_id] : []), department_names: access?.department_names || (staff.department_name ? [staff.department_name] : []), permissions: access?.permissions || [], default_landing_page: access?.default_landing_page || null };
      });
      setStaffList(mapped);
    } catch {
      setError('スタッフ情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const fetchDetail = useCallback(async (staffId: string) => {
    setDetailLoading(true);
    try {
      const [termsResult, docsResult] = await Promise.all([
        supabase
          .from('staff_employment_terms')
          .select('employment_type, hourly_wage_yen, monthly_salary_yen, effective_from, effective_to')
          .eq('staff_id', staffId)
          .order('effective_from', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('staff_document_status')
          .select('mynumber_submitted, passport_required, passport_submitted, passport_expiry, residence_status, residence_card_required, residence_card_submitted, residence_card_expiry')
          .eq('staff_id', staffId)
          .maybeSingle(),
      ]);

      setDetailData({
        employment_terms: (termsResult.data as Record<string, unknown>) || null,
        document_status: (docsResult.data as Record<string, unknown>) || null,
      });
    } catch {
      setDetailData({ employment_terms: null, document_status: null });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleRowClick = (staffId: string) => {
    if (expandedStaffId === staffId) {
      setExpandedStaffId(null);
      return;
    }
    setExpandedStaffId(staffId);
    fetchDetail(staffId);
  };

  const handleToggleActive = async (staff: StaffMember) => {
    if (staff.id === currentProfile?.id) return;

    const newActive = !staff.is_active;
    const action = newActive ? '有効化' : '無効化';

    if (!window.confirm(`${staff.full_name} を${action}しますか？`)) return;

    setTogglingId(staff.id);
    try {
      const { data, error: updateError } = await supabase.rpc('admin_set_staff_access', {
        p_staff_id: staff.id,
        p_role_name: staff.role_name,
        p_is_active: newActive,
        p_hired_at: staff.hired_at,
      });
      const result = data as { success?: boolean } | null;

      if (updateError || !result?.success) {
        setStatusMessage({ type: 'error', text: `${action}に失敗しました` });
        return;
      }

      setStaffList((prev) =>
        prev.map((s) => (s.id === staff.id ? { ...s, is_active: newActive } : s))
      );
      setStatusMessage({ type: 'success', text: `${staff.full_name} を${action}しました` });
    } catch {
      setStatusMessage({ type: 'error', text: `${action}に失敗しました` });
    } finally {
      setTogglingId(null);
    }
  };

  const handleInviteSuccess = () => {
    setShowInvite(false);
    fetchStaff();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('ja-JP');
  };

  const formatYen = (amount: number | null | undefined) => {
    if (amount == null) return '--';
    return `¥${amount.toLocaleString()}`;
  };

  return (
    <div className="max-w-7xl">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground-900 font-heading">スタッフ管理</h2>
          <p className="text-sm text-foreground-600 mt-1">スタッフの一覧表示・招待・管理</p>
        </div>
        <button
          onClick={() => { setShowInvite(true); setStatusMessage(null); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-user-add-line text-sm"></i>
          </span>
          スタッフを招待
        </button>
      </div>

      {/* Status Message */}
      {statusMessage && (
        <div className={`mb-4 flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm ${
          statusMessage.type === 'success' ? 'bg-accent-100 text-accent-800' : 'bg-red-50 text-red-700'
        }`}>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={`${statusMessage.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} text-sm`}></i>
          </span>
          {statusMessage.text}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex flex-col items-center gap-3 py-12">
          <p className="text-sm text-foreground-700">{error}</p>
          <button
            onClick={fetchStaff}
            className="px-4 py-2 rounded-lg bg-primary-100/80 text-primary-800 text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            再試行
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && !error && (
        <div className="flex items-center justify-center py-12">
          <span className="w-6 h-6 flex items-center justify-center">
            <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin"></i>
          </span>
        </div>
      )}

      {/* Staff List */}
      {!loading && !error && (
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-100">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">氏名</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">所属部署</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">役割</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">システム権限</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">電話番号</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">入社日</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider w-28">ステータス</th>
                </tr>
              </thead>
              <tbody>
                {staffList.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center px-5 py-12 text-sm text-foreground-500">
                      まだスタッフが登録されていません
                    </td>
                  </tr>
                ) : (
                  staffList.map((staff) => (
                    <>
                      <tr
                        key={staff.id}
                        onClick={() => handleRowClick(staff.id)}
                        className={`border-b border-background-200/70 hover:bg-background-100 transition-colors cursor-pointer ${
                          expandedStaffId === staff.id ? 'bg-background-100' : ''
                        } ${!staff.is_active ? 'opacity-60' : ''}`}
                      >
                        <td className="px-5 py-3.5">
                          <div>
                            <p className="font-medium text-foreground-900">{staff.full_name}</p>
                            <p className="text-xs text-foreground-500 mt-0.5">{staff.email}</p>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-foreground-700">{staff.department_names.length ? staff.department_names.join(' / ') : '--'}</td>
                        <td className="px-5 py-3.5 text-foreground-700">{staff.job_title || '--'}</td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            staff.access_level === 'super_admin'
                              ? 'bg-accent-100 text-accent-800'
                              : staff.access_level === 'operations_manager'
                                ? 'bg-primary-100 text-primary-800'
                                : 'bg-secondary-100 text-secondary-800'
                          }`}>
                            {accessLevelLabel(staff.access_level)}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-foreground-700">{staff.phone || '--'}</td>
                        <td className="px-5 py-3.5 text-foreground-700">{formatDate(staff.hired_at)}</td>
                        <td className="px-5 py-3.5 text-center">
                          {staff.id === currentProfile?.id ? (
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                              staff.is_active ? 'bg-accent-100 text-accent-800' : 'bg-red-50 text-red-700'
                            }`}>
                              {staff.is_active ? '有効' : '無効'}
                            </span>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleToggleActive(staff); }}
                              disabled={togglingId === staff.id}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                                staff.is_active
                                  ? 'bg-accent-100 text-accent-800 hover:bg-red-100 hover:text-red-700'
                                  : 'bg-red-50 text-red-700 hover:bg-accent-100 hover:text-accent-800'
                              }`}
                            >
                              {togglingId === staff.id ? (
                                <span className="w-3 h-3 flex items-center justify-center">
                                  <i className="ri-loader-4-line animate-spin"></i>
                                </span>
                              ) : (
                                staff.is_active ? '有効' : '無効'
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                      {/* Detail Row */}
                      {expandedStaffId === staff.id && (
                        <tr key={`${staff.id}-detail`}>
                          <td colSpan={7} className="px-5 py-0 bg-background-100/50">
                            <div className="py-4">
                              {detailLoading ? (
                                <div className="flex items-center justify-center py-4">
                                  <span className="w-5 h-5 flex items-center justify-center">
                                    <i className="ri-loader-4-line animate-spin text-primary-500"></i>
                                  </span>
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div className="bg-background-50 rounded-lg border border-background-200/70 p-4">
                                    <div className="flex items-center justify-between mb-3"><h4 className="text-sm font-semibold text-foreground-800">基本情報・所属・権限</h4><div className="flex gap-3"><button onClick={(event) => { event.stopPropagation(); setProfileEditStaff(staff); }} className="text-xs font-medium text-primary-700">基本情報</button><button onClick={(event) => { event.stopPropagation(); setOrganizationStaff(staff); }} className="text-xs font-medium text-primary-700">所属・権限</button></div></div>
                                    <dl className="space-y-2 text-sm"><div className="flex justify-between gap-3"><dt className="text-foreground-600">部門</dt><dd className="font-medium text-right">{staff.department_names.length ? staff.department_names.join(' / ') : '--'}</dd></div><div className="flex justify-between"><dt className="text-foreground-600">役職</dt><dd className="font-medium">{staff.job_title || '--'}</dd></div><div className="flex justify-between"><dt className="text-foreground-600">権限</dt><dd className="font-medium">{accessLevelLabel(staff.access_level)}</dd></div></dl>
                                    {staff.access_level !== 'super_admin' && <button onClick={(event) => { event.stopPropagation(); setShiftPermissionStaff(staff); }} className="mt-3 w-full rounded-lg bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-800">部門別シフト作成権限を設定</button>}
                                  </div>
                                  {/* Employment Terms */}
                                  <div className="bg-background-50 rounded-lg border border-background-200/70 p-4">
                                    <div className="flex items-center justify-between mb-3">
                                      <h4 className="text-sm font-semibold text-foreground-800">雇用条件</h4>
                                      <button onClick={(event) => { event.stopPropagation(); setPayStaff(staff); }} className="text-xs font-medium text-primary-700 hover:text-primary-900">給与条件を変更</button>
                                    </div>
                                    {detailData.employment_terms ? (
                                      <dl className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                          <dt className="text-foreground-600">雇用形態</dt>
                                          <dd className="text-foreground-900 font-medium">
                                            {(detailData.employment_terms as Record<string, unknown>).employment_type === 'hourly' ? '時給制' : '月給制'}
                                          </dd>
                                        </div>
                                        <div className="flex justify-between">
                                          <dt className="text-foreground-600">時給</dt>
                                          <dd className="text-foreground-900 font-medium">{formatYen((detailData.employment_terms as Record<string, unknown>).hourly_wage_yen as number | null)}</dd>
                                        </div>
                                        <div className="flex justify-between">
                                          <dt className="text-foreground-600">月給</dt>
                                          <dd className="text-foreground-900 font-medium">{formatYen((detailData.employment_terms as Record<string, unknown>).monthly_salary_yen as number | null)}</dd>
                                        </div>
                                        <div className="flex justify-between">
                                          <dt className="text-foreground-600">適用開始日</dt>
                                          <dd className="text-foreground-900 font-medium">{formatDate((detailData.employment_terms as Record<string, unknown>).effective_from as string | null)}</dd>
                                        </div>
                                      </dl>
                                    ) : (
                                      <p className="text-sm text-foreground-500">雇用条件なし</p>
                                    )}
                                  </div>

                                  {/* Document Status */}
                                  <div className="bg-background-50 rounded-lg border border-background-200/70 p-4">
                                    <h4 className="text-sm font-semibold text-foreground-800 mb-3">書類状況</h4>
                                    {detailData.document_status ? (
                                      <dl className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                          <dt className="text-foreground-600">マイナンバー</dt>
                                          <dd>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                              (detailData.document_status as Record<string, unknown>).mynumber_submitted
                                                ? 'bg-accent-100 text-accent-800'
                                                : 'bg-red-50 text-red-700'
                                            }`}>
                                              {(detailData.document_status as Record<string, unknown>).mynumber_submitted ? '提出済' : '未提出'}
                                            </span>
                                          </dd>
                                        </div>
                                        <div className="flex justify-between">
                                          <dt className="text-foreground-600">パスポート</dt>
                                          <dd className="text-right">
                                            <p className="text-xs text-foreground-600 mb-1">
                                              必要: {(detailData.document_status as Record<string, unknown>).passport_required ? '有' : '無'}
                                              {' / '}
                                              提出: {(detailData.document_status as Record<string, unknown>).passport_submitted ? '済' : '未'}
                                            </p>
                                            {(detailData.document_status as Record<string, unknown>).passport_expiry && (
                                              <span className="text-xs text-foreground-500">
                                                有効期限: {formatDate((detailData.document_status as Record<string, unknown>).passport_expiry as string | null)}
                                              </span>
                                            )}
                                          </dd>
                                        </div>
                                        <div className="flex justify-between">
                                          <dt className="text-foreground-600">在留資格</dt>
                                          <dd className="text-foreground-900 font-medium">
                                            {((detailData.document_status as Record<string, unknown>).residence_status as string) || '--'}
                                          </dd>
                                        </div>
                                        <div className="flex justify-between">
                                          <dt className="text-foreground-600">在留カード</dt>
                                          <dd className="text-right">
                                            <p className="text-xs text-foreground-600 mb-1">
                                              必要: {(detailData.document_status as Record<string, unknown>).residence_card_required ? '有' : '無'}
                                              {' / '}
                                              提出: {(detailData.document_status as Record<string, unknown>).residence_card_submitted ? '済' : '未'}
                                            </p>
                                            {(detailData.document_status as Record<string, unknown>).residence_card_expiry && (
                                              <span className="text-xs text-foreground-500">
                                                有効期限: {formatDate((detailData.document_status as Record<string, unknown>).residence_card_expiry as string | null)}
                                              </span>
                                            )}
                                          </dd>
                                        </div>
                                      </dl>
                                    ) : (
                                      <p className="text-sm text-foreground-500">書類情報なし</p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="relative bg-background-50 rounded-xl border border-background-200/70 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background-50 border-b border-background-200/70 px-5 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-base font-semibold text-foreground-900">スタッフを招待</h3>
              <button
                onClick={() => setShowInvite(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-close-line text-foreground-700"></i>
                </span>
              </button>
            </div>
            <div className="p-5">
              <InviteForm
                onSuccess={handleInviteSuccess}
                onCancel={() => setShowInvite(false)}
              />
            </div>
          </div>
        </div>
      )}
      {payStaff && <EmploymentTermModal
        staffId={payStaff.id}
        staffName={payStaff.full_name}
        onClose={() => setPayStaff(null)}
        onSuccess={() => {
          setPayStaff(null);
          setStatusMessage({ type: 'success', text: '給与条件を保存しました' });
          if (expandedStaffId) fetchDetail(expandedStaffId);
        }}
      />}
      {organizationStaff && <StaffOrganizationModal
        staffId={organizationStaff.id}
        staffName={organizationStaff.full_name}
        initialDepartmentId={organizationStaff.department_id}
        initialDepartmentIds={organizationStaff.department_ids}
        initialJobTitle={organizationStaff.job_title}
        initialAccessLevel={organizationStaff.access_level}
        initialPermissions={organizationStaff.permissions}
        initialLandingPage={organizationStaff.default_landing_page}
        onClose={() => setOrganizationStaff(null)}
        onSuccess={() => {
          setOrganizationStaff(null);
          setStatusMessage({ type: 'success', text: '所属・役職・システム権限を保存しました' });
          fetchStaff();
        }}
      />}
      {shiftPermissionStaff && <ShiftPermissionModal
        staffId={shiftPermissionStaff.id}
        staffName={shiftPermissionStaff.full_name}
        onClose={() => setShiftPermissionStaff(null)}
        onSuccess={() => {
          setShiftPermissionStaff(null);
          setStatusMessage({ type: 'success', text: 'シフト作成権限を保存しました' });
        }}
      />}
      {profileEditStaff && <StaffProfileEditModal
        staffId={profileEditStaff.id}
        staffName={profileEditStaff.full_name}
        email={profileEditStaff.email}
        onClose={() => setProfileEditStaff(null)}
        onSuccess={() => {
          setProfileEditStaff(null);
          setStatusMessage({ type: 'success', text: '基本情報・書類状況を保存しました' });
          fetchStaff();
          if (expandedStaffId) fetchDetail(expandedStaffId);
        }}
      />}
    </div>
  );
}
